/**
 * Helpers for resolving the current user's email signature.
 *
 * In Lovable Preview the signature lives in localStorage under
 * `coffee-preview-email-signature`. In production the signature comes from
 * the backend `User.emailSignature` field.
 *
 * `appendSignatureToBody` is intentionally idempotent — calling it twice on
 * the same body will not duplicate the signature. This lets callers safely
 * append the signature at multiple points (preview rendering AND send time)
 * without worrying about double-appending.
 */

const PREVIEW_SIG_KEY = "coffee-preview-email-signature";
const SIG_MARKER = "<!-- coffee:signature -->";

export function getPreviewEmailSignature(): string {
  if (typeof window === "undefined") return "";
  try {
    return localStorage.getItem(PREVIEW_SIG_KEY) || "";
  } catch {
    return "";
  }
}

export function setPreviewEmailSignature(value: string): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(PREVIEW_SIG_KEY, value || "");
    window.dispatchEvent(new Event("preview-email-signature-changed"));
  } catch {
    // ignore
  }
}

/**
 * Strip basic HTML tags so a rich-text signature can be inserted into a
 * plain-text email body. Preserves line breaks for <br> and block tags.
 */
export function signatureToPlainText(html: string): string {
  if (!html) return "";
  // If it's already plain text (no tags), return as-is
  if (!/<[^>]+>/.test(html)) return html.trim();
  return html
    .replace(/<br\s*\/?>(\n)?/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
    .replace(/<li[^>]*>/gi, "• ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Append the signature to the end of a plain-text email body if it isn't
 * already present. Returns the original body unchanged when no signature
 * is configured.
 */
export function appendSignatureToBody(body: string, signature: string): string {
  const sig = signatureToPlainText(signature);
  if (!sig) return body;
  // Already appended — bail out
  if (body.includes(SIG_MARKER)) return body;
  // Heuristic: if the trimmed signature already appears at the tail of the
  // body, don't add it again (covers users who typed it inline).
  const trimmedBody = body.trimEnd();
  if (trimmedBody.endsWith(sig.trimEnd())) return body;
  const separator = trimmedBody.length ? "\n\n" : "";
  return `${trimmedBody}${separator}${sig}\n${SIG_MARKER}`;
}

/**
 * Strip the internal signature marker before sending so it never reaches
 * the recipient.
 */
export function stripSignatureMarker(body: string): string {
  return body.split(SIG_MARKER).join("").trimEnd();
}